import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase/config";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

function waitForAuthenticatedUser(timeoutMs = 3000) {
  const auth = getAuth(app);

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error("Timed out waiting for Firebase auth state"));
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        return;
      }

      clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    });
  });
}

async function getAffiliateAccessToken() {
  const loginMethod = localStorage.getItem("loginMethod");

  if (loginMethod === "google") {
    try {
      const auth = getAuth(app);
      const user = auth.currentUser || (await waitForAuthenticatedUser());
      if (user) {
        return await user.getIdToken();
      }
    } catch (error) {
      console.error("Không thể lấy Firebase ID token mới:", error);
    }
  }

  return localStorage.getItem("userAccessToken");
}

async function buildRequestHeaders({ requiresAuth = false } = {}) {
  const headers = {
    "Content-Type": "application/json",
  };

  if (!requiresAuth) {
    return headers;
  }

  const accessToken = await getAffiliateAccessToken();
  if (!accessToken) {
    throw new Error("Thiếu token đăng nhập để gọi API affiliate");
  }

  return {
    ...headers,
    Authorization: `Bearer ${accessToken}`,
  };
}

async function requestAffiliateApi(path, options = {}) {
  const { requiresAuth = false, ...fetchOptions } = options;
  const headers = await buildRequestHeaders({ requiresAuth });

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers: {
      ...headers,
      ...(fetchOptions.headers || {}),
    },
  });

  return response.json();
}

export const trackAffiliateClick = async (
  affiliateId,
  campaign = "",
  source = "",
  url = ""
) => {
  try {
    return await requestAffiliateApi("/api/affiliate/track-click", {
      method: "POST",
      body: JSON.stringify({
        affiliateId,
        campaign,
        source,
        url,
      }),
    });
  } catch (error) {
    console.error("Lỗi khi ghi nhận click affiliate:", error);
    throw error;
  }
};

export const trackAffiliateSignup = async (
  userId,
  affiliateId,
  campaign = "",
  source = "",
  isNewUser = false
) => {
  try {
    return await requestAffiliateApi("/api/affiliate/track-signup", {
      method: "POST",
      requiresAuth: true,
      body: JSON.stringify({
        userId,
        affiliateId,
        campaign,
        source,
        isNewUser,
      }),
    });
  } catch (error) {
    console.error("Lỗi khi ghi nhận đăng ký affiliate:", error);
    throw error;
  }
};

export const completeAffiliateSignup = async (userId, affiliateId) => {
  console.warn(
    "completeAffiliateSignup đã bị vô hiệu hóa ở frontend. Flow production chốt affiliate ngay khi user mới được tạo.",
    { userId, affiliateId }
  );
  return {
    success: false,
    message:
      "Endpoint complete-signup đã bị vô hiệu hóa. Flow production chốt affiliate ngay khi tạo user mới.",
    error: "AFFILIATE_COMPLETE_SIGNUP_DEPRECATED",
  };
};

export const trackAffiliatePayment = async (
  userId,
  affiliateId,
  amount,
  campaign = "",
  source = ""
) => {
  console.warn(
    "trackAffiliatePayment đã bị vô hiệu hóa ở frontend. Hoa hồng affiliate chỉ được xử lý từ webhook thanh toán.",
    { userId, affiliateId, amount, campaign, source }
  );
  return {
    success: false,
    message:
      "Endpoint track-payment đã bị vô hiệu hóa. Hoa hồng affiliate chỉ được xử lý từ webhook thanh toán.",
    error: "AFFILIATE_TRACK_PAYMENT_DEPRECATED",
  };
};

export const getAffiliateStats = async (userId) => {
  try {
    return await requestAffiliateApi(`/api/affiliate/stats/${userId}`, {
      method: "GET",
      requiresAuth: true,
    });
  } catch (error) {
    console.error("Lỗi khi lấy thống kê affiliate:", error);
    throw error;
  }
};

export const getAffiliateReferredTransactions = async (userId) => {
  try {
    return await requestAffiliateApi(
      `/api/affiliate/referred-transactions/${userId}`,
      {
        method: "GET",
        requiresAuth: true,
      }
    );
  } catch (error) {
    console.error("Lỗi khi lấy giao dịch affiliate:", error);
    throw error;
  }
};

export const markNotificationRead = async (userId, notificationId) => {
  console.warn(
    "markNotificationRead chưa có backend production tương ứng và đã bị vô hiệu hóa ở frontend.",
    { userId, notificationId }
  );
  return {
    success: false,
    message:
      "Chức năng đánh dấu thông báo affiliate đã đọc chưa được hỗ trợ ở production.",
    error: "AFFILIATE_NOTIFICATION_UNAVAILABLE",
  };
};
